import type { ComponentProps, SubmitEvent } from "react"
import { useEffect, useMemo, useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Checkbox from "@/v2-ui/checkbox"
import Icon from "@/v2-ui/icon"
import Label from "@/v2-ui/label"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/v2-ui/inputGroup"
import {
  getPasswordValidity,
  generatePassword,
} from "@/v2-backoffice-client/user/resetPassword/user.resetPassword.utils"

export type UserResetPasswordFormValue = {
  password: string
  /**
   * Temporary password: the identity directory refuses to sign the user in
   * until they have chosen a new one (Fusion routes them to the in-app
   * reset, the hosted page runs "Update your password"). Off sets a
   * permanent password.
   */
  forceChangePasswordNextSignIn: boolean
}

type BaseProps = Omit<ComponentProps<"form">, "onSubmit">
export type UserResetPasswordFormProps = BaseProps & {
  onSubmit(
    value: UserResetPasswordFormValue,
    e: SubmitEvent<HTMLFormElement>,
  ): void
  onValidityChange?(isValid: boolean): void
}

function UserResetPasswordForm(props: UserResetPasswordFormProps) {
  const { onSubmit, onValidityChange, ...formProps } = props
  const [password, setPassword] = useState("")
  const [forceChange, setForceChange] = useState(true)

  const { results, isValid } = useMemo(() => {
    return getPasswordValidity(password)
  }, [password])

  useEffect(() => {
    onValidityChange?.(isValid)
  }, [isValid, onValidityChange])

  const showRules = password.length > 0

  return (
    <form
      id="reset-password-form"
      className="space-y-4 py-4"
      onSubmit={(e) => {
        onSubmit({ password, forceChangePasswordNextSignIn: forceChange }, e)
      }}
      {...formProps}
    >
      <div className="space-y-2">
        <Label htmlFor="newPassword">New Password</Label>
        <InputGroup size="md">
          <InputGroupInput
            id="newPassword"
            type="text"
            required
            autoFocus
            placeholder="Enter temporary password..."
            value={password}
            onChange={(e) => {
              setPassword(e.target.value)
            }}
          />
          <InputGroupAddon align="inline-end">
            <InputGroupButton
              size="md"
              variant="ghost"
              onClick={() => {
                setPassword(generatePassword())
              }}
            >
              <Icon src="shuffle" size={14} />
              Generate
            </InputGroupButton>
          </InputGroupAddon>
        </InputGroup>
      </div>

      {showRules && (
        <ul className="space-y-1 text-sm">
          {results.map((rule) => (
            <li
              key={rule.label}
              className={cn(
                "flex items-center gap-2",
                rule.passed ? "text-emerald-600" : "text-muted-foreground",
              )}
            >
              <Icon
                className={!rule.passed ? "opacity-40" : undefined}
                src={rule.passed ? "check" : "x"}
              />
              {rule.label}
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-start gap-2">
        <Checkbox
          id="reset-password-force-change"
          className="mt-0.5"
          checked={forceChange}
          onCheckedChange={(checked) => {
            setForceChange(checked === true)
          }}
          data-feature-code="UI_BackofficeUser_ResetPassword_ForceChange"
        />
        <div className="space-y-1">
          <Label htmlFor="reset-password-force-change">
            Require a new password at next sign-in
          </Label>
          <p className="text-sm text-muted-foreground">
            Makes this a temporary password: the user signs in with it once and
            is asked to choose their own. Turn off to set a permanent password.
          </p>
        </div>
      </div>
    </form>
  )
}

export default UserResetPasswordForm
